import * as React from "react";
import { Formik } from "formik";
import { useHistory, useLocation } from "react-router";
import { get } from "lodash";

import { Prompt, Media, PromptType, MediaType, Category } from "@/interfaces";
import { PromptComponent } from "@/components/Forms/Prompt";
import { URLS } from "@/utils";

import { validationSchema } from "./validations";
interface OwnProps {
  /**
   * Prompt Info
   */
  prompt?: Prompt;
  /**
   * Boolean to indicate if the form is editable (just some fields)
   */
  modeEditable: boolean;
  disabled: boolean;
  pageErrors?: string[];
  /** Shown instead of the media uploader and the Reels/Feed selector (a dynamic prompt's design). */
  mediaSlot?: React.ReactNode;
  /** Shown instead of the caption box when the caption isn't the coach's to write; hashtags stay. */
  captionNote?: React.ReactNode;

  updatingHandler: (values: any) => void;
  onContentLoading: (value: boolean) => void;
}

type Props = OwnProps;

const IntegratedPrompt: React.FC<Props> = ({
  modeEditable,
  disabled,
  prompt,
  updatingHandler,
  onContentLoading,
  pageErrors,
  mediaSlot,
  captionNote,
}) => {
  const history = useHistory();
  const location = useLocation<{ category?: Category }>();
  return (
    <div>
      <Formik
        validateOnBlur={false}
        validateOnChange={true}
        validateOnMount={!!prompt?.promptId}
        initialValues={{
          promptId: get(prompt, "promptId", null),
          title: get(prompt, "title", ""),
          message: prompt?.message,
          hashtags: get(prompt, "hashtags", ""),
          allowShortLink: get(prompt, "allowShortLink", true),
          smartLinkId: get(prompt, "smartLinkId", null),
          linkType: get(prompt, "smartLinkId", null) ? "SMART_LINK" : "PLAYER_LINK",
          allowComplianceDisclaimer: get(prompt, "allowComplianceDisclaimer", true),
          allowHashtags: get(prompt, "allowHashtags", true),
          richMessage: get(prompt, "richMessage", get(prompt, "message", "")),
          post: get(prompt, "post", {
            media: [],
          }),
          status: get(prompt, "status", "DRAFT"),
          categories: get(
            prompt,
            "categories",
            location.state && location.state["category"] ? [location.state["category"]] : []
          ),
          type: get(prompt, "type", "FEED"),
        }}
        validationSchema={validationSchema}
        onSubmit={async () => {
          // Action removed because the client wants
          // to use TitleSection action to save the prompt
        }}
      >
        {formikProps => (
          <>
            <FormikObserver values={formikProps.values} updatingHandler={updatingHandler} />
            <PromptComponent
              prompt={prompt}
              messages={{
                error:
                  pageErrors && pageErrors.length > 0 ? pageErrors : (Object.values(formikProps.errors) as string[]),
              }}
              handleCancel={() => {
                history.push(URLS.coach.prompts);
              }}
              handleSubmit={formikProps.handleSubmit}
              handleChange={formikProps.handleChange}
              handleSetFieldValue={formikProps.setFieldValue}
              onUploading={onContentLoading}
              onNewMedia={(newMedia: Media[]) => {
                const newType: MediaType | undefined =
                  formikProps.values.type === PromptType.REELS
                    ? "REELS"
                    : newMedia.length > 1
                    ? "MIX"
                    : newMedia[0]
                    ? newMedia[0].category
                    : undefined;

                formikProps.setFieldValue("post", {
                  media: newMedia,
                  mediaGroupId: get(formikProps.values.post, "mediaGroupId", null),
                  type: newType,
                });
              }}
              disabled={disabled}
              mediaSlot={mediaSlot}
              captionNote={captionNote}
              modeEditable={modeEditable}
              values={formikProps.values}
              isSubmitting={formikProps.isSubmitting}
            />
          </>
        )}
      </Formik>
    </div>
  );
};

const FormikObserver: React.FC<{ values: any; updatingHandler: (values: any) => void }> = ({
  values,
  updatingHandler,
}) => {
  React.useEffect(() => {
    updatingHandler(values);
  }, [values, updatingHandler]);
  return null;
};

export const PromptForm = IntegratedPrompt;
